import {
  Box,
  LinearProgress,
  linearProgressClasses,
  Typography
} from '@mui/material'
import PasswordField from 'components/PasswordField'
import React, { useId, useState } from 'react'
import {
  meetsPasswordPolicy,
  passwordProblems,
  passwordRules
} from 'utils/passwordPolicy'
import theme from 'utils/theme'
import zxcvbn from 'zxcvbn'

export interface PasswordProgressProps {
  autoComplete: string
  label: string
  required?: boolean
  // Also check the customer pool's password rules (utils/passwordPolicy): a
  // checklist under the field, `hasError` until every rule is met, and never
  // "Nice" or "Excellent" for a password Cognito would refuse.
  policy?: boolean
  onChange: (password: string, hasError?: boolean) => void
}

// The meter's own suggestion line, reused for the checklist.
const lineSx = {
  fontSize: 12,
  fontWeight: 400,
  margin: theme.spacing(0.5, 1.75, 0)
}

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
} as const

export default function PasswordProgress(
  props: PasswordProgressProps
): JSX.Element {
  const {
    autoComplete,
    label,
    required = false,
    policy = false,
    onChange
  } = props
  const rulesId = useId()

  const [password, setPassword] = useState<string>('')
  const [passwordProgress, setPasswordProgress] = useState<{
    strength: number
    suggestions: string[]
    score: number
  }>({
    strength: 0,
    suggestions: [],
    score: 0
  })
  const [error, setError] = useState<string>('')

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value
    const inputPassword = zxcvbn(value)
    const policyMet = !policy || meetsPasswordPolicy(value)
    setPassword(value)
    onChange(value, inputPassword.score < 3 || !policyMet)
    if (inputPassword.score < 3) {
      setError(inputPassword.feedback.suggestions[0])
      setPasswordProgress(prev => ({
        ...prev,
        strength: inputPassword.guesses_log10 * 8,
        suggestions: inputPassword.feedback.suggestions.join(' ').split('. '),
        score: inputPassword.score
      }))
    } else if (!policyMet) {
      // Strong by zxcvbn, but Cognito would refuse it: no praise and a red
      // bar, as for a weak password. The checklist says what is missing.
      setError('')
      setPasswordProgress(prev => ({
        ...prev,
        strength: Math.min(inputPassword.guesses_log10 * 8, 100),
        suggestions: [],
        score: 2
      }))
    } else {
      setError('')
      switch (inputPassword.score) {
        case 3:
          return setPasswordProgress(prev => ({
            ...prev,
            strength: 70,
            score: inputPassword.score,
            suggestions: ['Nice 👍']
          }))
        case 4:
          return setPasswordProgress(prev => ({
            ...prev,
            strength: 100,
            score: inputPassword.score,
            suggestions: ['Excellent 🔒']
          }))
      }
    }
  }

  const handleBlurInput = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => {
    setError(prev => (!e.target.value ? `${label} is required` : prev))
  }

  const rules = policy ? passwordRules(password) : []
  const problems = policy ? passwordProblems(password) : []

  return (
    <React.Fragment>
      <PasswordField
        autoComplete={autoComplete}
        fullWidth={true}
        label={label}
        required={required}
        error={!!error}
        helperText={!passwordProgress.strength && error ? error : ''}
        sx={{ background: 'none' }}
        inputProps={policy ? { 'aria-describedby': rulesId } : undefined}
        onChange={handleChangeInput}
        onBlur={handleBlurInput}
      />
      {passwordProgress.strength > 0 && (
        <Box sx={{ marginTop: '8px' }}>
          <LinearProgress
            variant='determinate'
            value={passwordProgress.strength}
            sx={{
              height: 8,
              borderRadius: 5,
              [`& .${linearProgressClasses.bar}`]: {
                background:
                  passwordProgress.score >= 3
                    ? theme.palette.green.main
                    : theme.palette.error.main
              }
            }}
          />

          {passwordProgress.suggestions.map((suggestion: string, index) => (
            <Typography
              key={index}
              sx={{
                ...lineSx,
                color:
                  passwordProgress.score >= 3
                    ? theme.palette.black.main
                    : theme.palette.error.main
              }}
            >
              {suggestion}
            </Typography>
          ))}
        </Box>
      )}
      {policy && (
        <Box id={rulesId} sx={{ marginTop: '8px' }}>
          <Typography sx={{ ...lineSx, color: theme.palette.black.main }}>
            Your password needs
          </Typography>
          <Box component='ul' sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rules.map(rule => (
              <Typography
                component='li'
                key={rule.key}
                data-rule={rule.key}
                data-met={rule.met}
                sx={{
                  ...lineSx,
                  color:
                    password && !rule.met
                      ? theme.palette.error.main
                      : theme.palette.black.main
                }}
              >
                {rule.label}
                <Box component='span' sx={visuallyHidden}>
                  {rule.met ? ', done' : ', still needed'}
                </Box>
              </Typography>
            ))}
            {problems.map(problem => (
              <Typography
                component='li'
                key={problem}
                sx={{ ...lineSx, color: theme.palette.error.main }}
              >
                {problem}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </React.Fragment>
  )
}
